import LangsRequest from '@/@api/langs'
import { ref, computed } from '@vue/composition-api'
// import store from '@/store'
// import { title } from '@core/utils/filter'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { debouncedWatch } from '@vueuse/core'

const langs = ref([])

export default function useLangs() {
  // Use toast
  const toast = useToast()

  const refLangListTable = ref(null)

  // const langs = ref([])

  // Table Handlers
  const tableColumns = [
    { key: 'id', label: '#', sortable: true },
    { label: 'ISO', key: 'iso', sortable: true },
    {
      label: 'Idioma',
      key: 'name',
      sortable: true,
      thStyle: { width: '100%' },
    },
    { label: 'Activo', key: 'status', sortable: true },
    { label: 'Acciones', key: 'actions' },
  ]
  const perPage = ref(10)
  const totalLangs = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('id')
  const isSortDirDesc = ref(true)
  const roleFilter = ref(null)
  const planFilter = ref(null)
  const statusFilter = ref(null)

  const dataMeta = computed(() => {
    const localItemsCount = refLangListTable.value ? refLangListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalLangs.value,
    }
  })

  const refetchData = () => {
    refLangListTable.value.refresh()
  }

  debouncedWatch([currentPage, perPage, searchQuery, roleFilter, planFilter, statusFilter], () => {
    refetchData()
  }, { debounce: 400 })

  const fetchLangs = (ctx, callback) => {
    LangsRequest.list({
      params: {
        q: searchQuery.value,
        paginate: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        role: roleFilter.value,
        plan: planFilter.value,
        status: statusFilter.value,
      },
    })
      .then(response => {
        const { data, total } = response.data.data

        callback(data.map(lang => ({ ...lang, loading: false })))
        totalLangs.value = total
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching langs list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  async function fetchLangsSelector(isMemo = false) {
    if (isMemo && langs.value.length) {
      return Promise.resolve(langs.value)
    }

    try {
      const { data } = await LangsRequest.select()

      const list = data.data.map(lang => ({
        id: lang.id,
        value: lang.iso?.toUpperCase() || lang.id,
        label: lang.name,
      }))

      langs.value = list

      return list
    } catch (error) {
      throw new Error(error)
    }
  }

  async function langStore(body) {
    try {
      const { data } = await LangsRequest.store(body)

      toast({
        component: ToastificationContent,
        props: {
          title: data.message,
          icon: 'CheckIcon',
          variant: 'success',
        },
      })
    } catch (error) {
      throw new Error(error)
    }
  }

  async function langUpdate(id, body) {
    try {
      const { data } = await LangsRequest.update(id, body)

      toast({
        component: ToastificationContent,
        props: {
          title: data.message,
          icon: 'CheckIcon',
          variant: 'success',
        },
      })
    } catch (error) {
      throw new Error(error)
    }
  }

  async function langDelete(id) {
    try {
      const { data } = await LangsRequest.destroy(id)

      toast({
        component: ToastificationContent,
        props: {
          title: data.message,
          icon: 'CheckIcon',
          variant: 'success',
        },
      })
    } catch (error) {
      throw new Error(error)
    }
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  fetchLangsSelector(true).then(list => {
    langs.value = list
  })

  const resolveIsoLang = iso => {
    if (!iso) return ''

    const dic = langs.value.reduce((acc, lang) => {
      acc[lang.value] = lang.label

      return acc
    }, {})

    return dic[iso] ?? iso
  }

  return {
    langs,
    fetchLangs,
    fetchLangsSelector,
    tableColumns,
    perPage,
    currentPage,
    totalLangs,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refLangListTable,

    resolveIsoLang,

    refetchData,
    langStore,
    langUpdate,
    langDelete,

    // Extra Filters
    roleFilter,
    planFilter,
    statusFilter,
  }
}
